import { useEffect, useRef } from 'react';
import {
	isRefreshDue,
	onPortalSessionStale,
	SESSION_CONFLICT_REFRESH_MIN_INTERVAL_MS,
	SESSION_REFRESH_MIN_INTERVAL_MS,
} from '../lib/portalSessionSync';
import { usePortalAuth } from './usePortalAuth';

/**
 * PD-16789 — keep the signed-in storefront's account list and active account
 * in step with the server while the shopper uses it. Mounted once, in
 * PortalLayout.
 *
 * Re-reads the session:
 * - when the window regains focus or the tab becomes visible again, at most
 *   once a minute — the moment someone returns to a storefront left open
 *   while staff changed their accounts in the CRM;
 * - when an API call answers 409 "No active client selected", which is what
 *   the server says after it forgets an account the user lost access to.
 *
 * The Select Account page (outside PortalLayout) refreshes on its own when
 * it opens and after a refused switch.
 */
export function usePortalSessionSync(): void {
	const { isAuthenticated, refreshSession } = usePortalAuth();
	// The session was just read when the layout mounted (sign-in or restore),
	// so the first focus refresh waits a full interval from here.
	const lastFocusRefreshAt = useRef<number>(Date.now());
	const lastConflictRefreshAt = useRef<number | null>(null);

	useEffect(() => {
		if (!isAuthenticated) {
			return undefined;
		}

		const refreshIfDue = (): void => {
			if (document.visibilityState === 'hidden') {
				return;
			}
			const now = Date.now();
			if (
				!isRefreshDue(
					lastFocusRefreshAt.current,
					now,
					SESSION_REFRESH_MIN_INTERVAL_MS,
				)
			) {
				return;
			}
			lastFocusRefreshAt.current = now;
			void refreshSession();
		};

		const stopListening = onPortalSessionStale(() => {
			const now = Date.now();
			if (
				!isRefreshDue(
					lastConflictRefreshAt.current,
					now,
					SESSION_CONFLICT_REFRESH_MIN_INTERVAL_MS,
				)
			) {
				return;
			}
			lastConflictRefreshAt.current = now;
			void refreshSession();
		});

		window.addEventListener('focus', refreshIfDue);
		document.addEventListener('visibilitychange', refreshIfDue);

		return (): void => {
			stopListening();
			window.removeEventListener('focus', refreshIfDue);
			document.removeEventListener('visibilitychange', refreshIfDue);
		};
	}, [isAuthenticated, refreshSession]);
}
